import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { googleLinkTacke } from './googleLinkovi';

/* Kartica iznad pina — sve što se sa jednom tačkom na mapi može uraditi.

   Dva reda teksta pa red radnji. Tekst odgovara na „gde sam kliknuo", radnje
   na „šta sad" — i zato su razdvojene tankom linijom, a ne samo razmakom.

   Ikone su ovde SVG, a ne PrimeIcons: lenjir i račvanje rute u tom skupu ne
   postoje, a zamena približnom ikonom (strelice, deljenje) menja značenje —
   dispečer bi „meri rastojanje" pročitao kao „razvuci" ili „podeli". */

const Lenjir = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1.5" y="8" width="21" height="8" rx="1.5" transform="rotate(-20 12 12)" />
        <path d="M6.2 8.9l1 2.6M9.6 7.7l.7 1.8M13 6.4l1 2.6M16.4 5.2l.7 1.8" />
    </svg>
);

const Racvanje = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
         stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="5.5" r="2.5" />
        <circle cx="18.5" cy="16" r="2" />
        <path d="M8 18.5h4.5A5.5 5.5 0 0 0 18 13V8.2" />
        <path d="M13.4 15.2l3 1" />
    </svg>
);

/* Strelica navigacije — isti znak koji nose sve velike mape, pa se ne mora
   uciti. Namerno POPUNJENA, za razliku od ostalih ikona na kartici: ovo je
   jedina radnja koja vodi VAN aplikacije, i to se vidi pre nego sto se
   procita opis. */
const Navigacija = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
        <path d="M21.4 3.1a1 1 0 0 0-1.2-1.2L3.3 8.2a1 1 0 0 0-.1 1.85l7.05 3.2a.6.6 0 0 1 .3.3l3.2 7.05a1 1 0 0 0 1.85-.1z"/>
    </svg>
);

const PopupPin = ({ tacka, mozeUpis, onDodajLokaciju, onVozilaUBlizini, onMeri, onTackaRute }) => {
    const { t } = useTranslation();
    const [meniOtvoren, setMeniOtvoren] = useState(false);
    const [kopirano, setKopirano] = useState(false);
    const okvir = useRef(null);

    /* Meni se zatvara klikom bilo gde drugde. Leaflet popup je van React
       stabla glavne stranice, pa se osluškuje dokument, ne roditelj. */
    useEffect(() => {
        if (!meniOtvoren) return;
        const van = (e) => { if (okvir.current && !okvir.current.contains(e.target)) setMeniOtvoren(false); };
        document.addEventListener('mousedown', van);
        return () => document.removeEventListener('mousedown', van);
    }, [meniOtvoren]);

    const koordinate = `${Number(tacka.lat).toFixed(5)}, ${Number(tacka.lng).toFixed(5)}`;

    const kopiraj = async () => {
        try {
            await navigator.clipboard.writeText(koordinate);
            setKopirano(true);
            setTimeout(() => setKopirano(false), 1600);
        } catch (e) {
            /* Bez HTTPS-a clipboard ne postoji — koordinate ostaju vidljive i
               mogu da se označe mišem, pa neuspeh ne treba prijavljivati. */
        }
    };

    const dugmad = [
        {
            kljuc: 'lokacija',
            ikona: <i className="pi pi-map-marker" />,
            naslov: t('gps.pinDodajLokaciju'),
            onClick: onDodajLokaciju,
            /* Bez prava na izmenu dugme ostaje vidljivo ali ugašeno: sakriveno
               dugme izgleda kao da mogućnost ne postoji, ugašeno kaže da
               postoji ali nije za ovog korisnika. */
            iskljuceno: !mozeUpis,
        },
        {
            kljuc: 'blizina',
            ikona: <i className="pi pi-crosshairs" />,
            naslov: t('gps.pinVozilaUBlizini'),
            onClick: onVozilaUBlizini,
        },
        {
            kljuc: 'merenje',
            ikona: <Lenjir />,
            naslov: t('gps.pinIzmeriUdaljenost'),
            onClick: onMeri,
        },
        {
            kljuc: 'google',
            ikona: <Navigacija />,
            naslov: t('gps.pinOtvoriUGoogle'),
            /* Deep link, ne API poziv — ne trosi kvotu i ne trazi kljuc.
                `noopener` je obavezan uz `_blank`: bez njega otvorena strana
                dobija pristup nasem prozoru preko `window.opener`. */
            onClick: () => {
                const veza = googleLinkTacke(tacka);
                if (veza) window.open(veza, '_blank', 'noopener,noreferrer');
            },
        },
    ];

    return (
        <div className="gps-pin" ref={okvir}>
            <div className="gps-pin-naslov">{tacka.naziv}</div>

            {!!tacka.opis && <div className="gps-pin-opis">{tacka.opis}</div>}

            <div className="gps-pin-koord">
                <span>{koordinate}</span>
                <button
                    type="button"
                    className="gps-pin-kopiraj"
                    onClick={kopiraj}
                    title={kopirano ? t('gps.pinKopirano') : t('gps.pinKopirajKoordinate')}
                    aria-label={t('gps.pinKopirajKoordinate')}
                >
                    <i className={kopirano ? 'pi pi-check' : 'pi pi-copy'} />
                </button>
            </div>

            <div className="gps-pin-linija" />

            <div className="gps-pin-radnje">
                {dugmad.map(d => (
                    <button
                        key={d.kljuc}
                        type="button"
                        className="gps-pin-dugme"
                        title={d.naslov}
                        aria-label={d.naslov}
                        disabled={d.iskljuceno}
                        onClick={d.onClick}
                    >
                        {d.ikona}
                    </button>
                ))}

                {/* Račvanje ne radi ništa samo po sebi — pita da li je tačka
                    početak ili kraj. Zato meni, a ne četvrto dugme sa jednim
                    značenjem. */}
                <div className="gps-pin-meni-omot">
                    <button
                        type="button"
                        className={`gps-pin-dugme${meniOtvoren ? ' gps-pin-dugme-aktivno' : ''}`}
                        title={t('gps.pinTackaRute')}
                        aria-label={t('gps.pinTackaRute')}
                        aria-haspopup="true"
                        aria-expanded={meniOtvoren}
                        onClick={() => setMeniOtvoren(o => !o)}
                    >
                        <Racvanje />
                    </button>

                    {meniOtvoren && (
                        <div className="gps-pin-meni" role="menu">
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => { setMeniOtvoren(false); onTackaRute('POCETAK'); }}
                            >
                                <i className="pi pi-send" />
                                {t('gps.pinPocetnaTacka')}
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => { setMeniOtvoren(false); onTackaRute('KRAJ'); }}
                            >
                                <i className="pi pi-flag" />
                                {t('gps.pinZavrsnaTacka')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PopupPin;
